import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Trash, X, Plus, ArrowLeft, User, Receipt, CashRegister, CreditCard, Scales,
  Printer, UserPlus, ClockCounterClockwise, FileText, LinkSimple, LinkSimpleBreak,
  Coin, Money, CheckCircle, Check, DeviceMobile, QrCode, Wallet, Bank,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MoneyInput from "@/components/MoneyInput";
import DateInput from "@/components/DateInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { fmtARS } from "@/lib/api";
import { imprimirTicketFiscal, imprimirTicketAlivio } from "@/lib/printTicket80";
import NumberInput from "@/components/ui/NumberInput";

export function DeliveryMostradorView({ pedidos, loading, onReload, onMarcarItem, onVolver, onImprimir, selectedPedido, onSelectPedido, onCargarAlPOS, onMarcarEntregado }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();

  // Agrupar por fecha_envio
  const grupos = { hoy: [], manana: [], otros: [] };
  for (const p of (pedidos || [])) {
    const f = (p.fecha_envio || "").slice(0, 10);
    if (f === todayISO) grupos.hoy.push(p);
    else if (f === tomorrowISO) grupos.manana.push(p);
    else grupos.otros.push(p);
  }

  const selectedInfo = selectedPedido
    ? (pedidos || []).find((p) => p.origen === selectedPedido.origen && String(p.id) === String(selectedPedido.id))
    : null;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      <div className="px-4 py-2 border-b border-[#6B1F24]/20 flex items-center gap-3 bg-[#F9F6F6]">
        <h2 className="text-[14px] font-heading font-bold text-[#6B1F24]">Mostrador · Delivery</h2>
        <span className="text-[11px] font-mono text-neutral-500 uppercase">
          {pedidos.length} pedido{pedidos.length !== 1 ? "s" : ""} pendientes
        </span>
        {selectedInfo && (
          <div
            className="flex items-center gap-2 px-3 py-1 rounded-md bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-mono"
            data-testid="mostrador-selected-badge"
          >
            <span className="uppercase tracking-widest">Escaneando balanza en</span>
            <b>#{selectedInfo.numero}</b>
            <span>· {selectedInfo.cliente_nombre || ""}</span>
            <button
              onClick={() => onSelectPedido && onSelectPedido(null)}
              className="ml-1 px-1.5 py-0.5 rounded-sm border border-amber-400 hover:bg-amber-200 text-[10px]"
              data-testid="mostrador-clear-select"
            >
              deseleccionar
            </button>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={onReload} className="h-7 text-xs" data-testid="mostrador-refresh">
            Actualizar
          </Button>
          <Button variant="outline" size="sm" onClick={onVolver} className="h-7 text-xs" data-testid="mostrador-volver">
            Volver al Mostrador
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {loading ? (
          <div className="p-8 text-center text-neutral-400 text-sm">Cargando…</div>
        ) : pedidos.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">
            No hay pedidos <b>A Mostrador</b> para hoy ni mañana.
          </div>
        ) : (
          <div className="space-y-6">
            <SeccionMostrador titulo="HOY" pedidos={grupos.hoy} onMarcarItem={onMarcarItem} onImprimir={onImprimir} selectedPedido={selectedPedido} onSelectPedido={onSelectPedido} onCargarAlPOS={onCargarAlPOS} onMarcarEntregado={onMarcarEntregado} />
            <SeccionMostrador titulo="MAÑANA" pedidos={grupos.manana} onMarcarItem={onMarcarItem} onImprimir={onImprimir} selectedPedido={selectedPedido} onSelectPedido={onSelectPedido} onCargarAlPOS={onCargarAlPOS} onMarcarEntregado={onMarcarEntregado} />
            {grupos.otros.length > 0 && (
              <SeccionMostrador titulo="OTROS" pedidos={grupos.otros} onMarcarItem={onMarcarItem} onImprimir={onImprimir} selectedPedido={selectedPedido} onSelectPedido={onSelectPedido} onCargarAlPOS={onCargarAlPOS} onMarcarEntregado={onMarcarEntregado} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SeccionMostrador({ titulo, pedidos, onMarcarItem, onImprimir, selectedPedido, onSelectPedido, onCargarAlPOS, onMarcarEntregado }) {
  return (
    <div>
      <div className="text-[13px] font-heading font-black tracking-widest text-[#6B1F24] pb-2 border-b-2 border-[#6B1F24]/20 mb-2">
        {titulo}
      </div>
      {pedidos.length === 0 ? (
        <div className="text-[11px] italic text-neutral-400 py-1">Sin pedidos en este día.</div>
      ) : (
        <div className="border border-neutral-200 rounded-md overflow-hidden">
          <table className="w-full text-[12px]" data-testid={`mostrador-tabla-${titulo.toLowerCase()}`}>
            <thead className="bg-neutral-100 border-b border-neutral-300">
              <tr className="text-left uppercase text-[10px] font-mono tracking-wider text-neutral-600">
                <th className="w-24 py-1.5 px-2"></th>
                <th className="w-24 py-1.5 px-2">Nº pedido</th>
                <th className="min-w-[160px] py-1.5 px-2">Cliente</th>
                <th className="w-24 py-1.5 px-2">Entrega</th>
                <th className="w-20 py-1.5 px-2 text-right">Cantidad</th>
                <th className="min-w-[180px] py-1.5 px-2">Producto</th>
                <th className="min-w-[110px] py-1.5 px-2">Especificación</th>
                <th className="w-28 py-1.5 px-2 text-right">Kg Reales</th>
                <th className="w-28 py-1.5 px-2 text-center">Sin stock</th>
                <th className="w-28 py-1.5 px-2 text-center">Terminado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {pedidos.map((p) => (
                <PedidoFilas key={`${p.origen}-${p.id}`} pedido={p} onMarcarItem={onMarcarItem} onImprimir={onImprimir} selectedPedido={selectedPedido} onSelectPedido={onSelectPedido} onCargarAlPOS={onCargarAlPOS} onMarcarEntregado={onMarcarEntregado} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PedidoFilas({ pedido, onMarcarItem, onImprimir, selectedPedido, onSelectPedido, onCargarAlPOS, onMarcarEntregado }) {
  const isTN = pedido.origen === "tn";
  const items = pedido.items || [];
  const isSelected = selectedPedido && selectedPedido.origen === pedido.origen && String(selectedPedido.id) === String(pedido.id);
  const rowBg = isSelected ? "bg-amber-50" : (isTN ? "bg-sky-50" : "bg-white");
  const hoverBg = isSelected ? "hover:bg-amber-100" : (isTN ? "hover:bg-sky-100" : "hover:bg-neutral-50");

  return items.length === 0 ? (
    <tr className={rowBg}>
      <td colSpan={10} className="px-2 py-2 text-neutral-400 italic text-[11px]">
        Sin ítems en este pedido.
      </td>
    </tr>
  ) : (
    items.map((it, idx) => (
      <FilaItem
        key={`${pedido.origen}-${pedido.id}-${idx}`}
        pedido={pedido}
        item={it}
        isFirst={idx === 0}
        rowSpan={items.length}
        rowBg={`${rowBg} ${hoverBg}`}
        isTN={isTN}
        onMarcarItem={onMarcarItem}
        onImprimir={onImprimir}
        isSelected={isSelected}
        onSelectPedido={onSelectPedido}
        onCargarAlPOS={onCargarAlPOS}
        onMarcarEntregado={onMarcarEntregado}
      />
    ))
  );
}

export function FilaItem({ pedido, item, isFirst, rowSpan, rowBg, isTN, onMarcarItem, onImprimir, isSelected, onSelectPedido, onCargarAlPOS, onMarcarEntregado }) {
  // Preferimos el kg_reales ya cargado; si aún es 0, mostramos el kilos_sugeridos
  // (calculado en backend como weight_TN × quantity para productos por kg).
  const kgInicial = () => {
    const real = Number(item.kg_reales || 0);
    if (real > 0) return String(real);
    const sug = Number(item.kilos_sugeridos || 0);
    if (sug > 0) return String(sug);
    return "";
  };
  const [kg, setKg] = useState(kgInicial());
  const [saving, setSaving] = useState(false);
  const estado = item.mostrador_estado || "pendiente";
  const isTerminado = estado === "terminado";
  const isSinStock = estado === "sin_stock";
  const tieneSugerencia = Number(item.kilos_sugeridos || 0) > 0 && Number(item.kg_reales || 0) === 0;

  // Sincronizar cuando el server manda un valor nuevo (poll silencioso)
  useEffect(() => {
    setKg(kgInicial());
    // eslint-disable-next-line
  }, [item.kg_reales, item.kilos_sugeridos]);

  const guardarKg = async () => {
    const num = kg === "" ? 0 : parseFloat(kg.replace(",", "."));
    if (Number.isNaN(num) || num < 0) return;
    if (num === Number(item.kg_reales || 0)) return; // sin cambios
    setSaving(true);
    try {
      await onMarcarItem({ origen: pedido.origen, pedido_id: pedido.id, item_idx: item.item_idx, kg_reales: num });
    } catch (_) {
      // onMarcarItem ya muestra el toast/modal correspondiente. Swallow para no
      // disparar el overlay de errores de React dev.
    } finally { setSaving(false); }
  };

  const toggleEstado = async (nuevo) => {
    setSaving(true);
    try {
      // Si ya está en ese estado, lo revierte a pendiente
      const target = estado === nuevo ? "pendiente" : nuevo;
      await onMarcarItem({ origen: pedido.origen, pedido_id: pedido.id, item_idx: item.item_idx, estado: target });
    } catch (_) {
      // idem
    } finally { setSaving(false); }
  };

  const qty = item.cantidad ?? item.quantity ?? 0;
  const desc = item.descripcion || item.name || item.producto || "";

  return (
    <tr className={`${rowBg} ${isTerminado ? "opacity-60" : ""}`} data-testid={`mostrador-fila-${pedido.numero}-${item.item_idx}`}>
      {isFirst ? (
        <>
          <td rowSpan={rowSpan} className="px-2 py-1 align-top">
            {(() => {
              const yaImpreso = !!pedido.ticket_impreso_at;
              const cnt = Number(pedido.ticket_impreso_count || 0);
              const impresoDate = yaImpreso ? new Date(pedido.ticket_impreso_at) : null;
              const impresoLabel = impresoDate
                ? impresoDate.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })
                : "";
              return (
                <button
                  onClick={() => onImprimir(pedido).catch(() => toast.error("No se pudo imprimir"))}
                  className={
                    "w-full flex flex-col items-center justify-center gap-0.5 px-2 py-1 rounded-md border transition "
                    + (yaImpreso
                      ? "border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900"
                      : "border-[#6B1F24]/25 bg-white hover:bg-[#F0D1B6]/40 text-[#6B1F24]")
                  }
                  title={
                    yaImpreso
                      ? `Ya impreso ${cnt > 1 ? `${cnt} veces ` : ""}(${impresoLabel}). Volver a imprimir?`
                      : "Imprimir ticket 80 mm"
                  }
                  data-testid={`mostrador-print-${pedido.numero}`}
                >
                  <span className="flex items-center gap-1 text-[11px] font-medium">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                    {yaImpreso ? "reimprimir" : "imprimir"}
                  </span>
                  {yaImpreso && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] uppercase font-mono tracking-wider text-amber-800"
                      data-testid={`mostrador-print-badge-${pedido.numero}`}
                    >
                      ⚠ ya impreso{cnt > 1 ? ` ×${cnt}` : ""} · {impresoLabel}
                    </span>
                  )}
                </button>
              );
            })()}
            {onSelectPedido && (
              <button
                onClick={() => onSelectPedido(isSelected ? null : { origen: pedido.origen, id: pedido.id })}
                className={
                  "mt-1 w-full flex items-center justify-center gap-1 px-2 h-6 rounded-md border text-[10px] font-heading font-bold uppercase tracking-wider transition "
                  + (isSelected
                    ? "border-amber-500 bg-amber-500 text-white"
                    : "border-amber-300 bg-white text-amber-700 hover:bg-amber-50")
                }
                title={isSelected ? "Este pedido está recibiendo los escaneos de balanza" : "Seleccionar para escanear etiquetas de balanza a este pedido"}
                data-testid={`mostrador-select-${pedido.numero}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="10" rx="1"/><path d="M6 7v10M10 7v10M14 7v10M18 7v10"/></svg>
                {isSelected ? "escaneando" : "escanear"}
              </button>
            )}
          </td>
          <td rowSpan={rowSpan} className={`px-2 py-1 align-top font-mono text-[12px] font-bold ${isTN ? "text-sky-800" : "text-amber-800"}`}>
            {pedido.numero}
          </td>
          <td rowSpan={rowSpan} className={`px-2 py-1 align-top font-medium ${isTN ? "text-sky-900" : "text-neutral-800"}`}>
            {pedido.cliente_nombre || "Sin cliente"}
            {pedido.cliente_telefono && (
              <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{pedido.cliente_telefono}</div>
            )}
          </td>
          <td rowSpan={rowSpan} className="px-2 py-1 align-top" data-testid={`mostrador-entrega-${pedido.numero}`}>
            {(() => {
              const et = (pedido.entrega_tipo || "").toLowerCase();
              const esPickup = et === "pickup";
              const esShip = et === "ship";
              const pagado = ((pedido.payment_status || "").toLowerCase() === "paid");
              // Detectar si todos los ítems del pedido están terminados/sin stock
              // (para habilitar visualmente el botón de acción)
              const allDone = (pedido.items || []).length > 0 && (pedido.items || []).every(
                (it) => (it.mostrador_estado || "pendiente") === "terminado" ||
                        (it.mostrador_estado || "pendiente") === "sin_stock"
              );
              return (
                <div className="flex flex-col gap-1">
                  <span
                    className={
                      "inline-flex items-center justify-center px-2 py-0.5 rounded-sm text-[9px] font-heading font-bold uppercase tracking-wider border " +
                      (esPickup
                        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                        : esShip
                          ? "bg-blue-50 text-blue-800 border-blue-300"
                          : "bg-neutral-50 text-neutral-500 border-neutral-200")
                    }
                    data-testid={`mostrador-entrega-badge-${pedido.numero}`}
                    title={pedido.entrega_opcion || (esPickup ? "Retiro en el local" : esShip ? "Envío a domicilio" : "Sin especificar")}
                  >
                    {esPickup ? "🏪 Retiro" : esShip ? "🚚 Envío" : "—"}
                  </span>
                  {esPickup && pagado && onMarcarEntregado && (
                    // Pedido de retiro YA pagado (Tienda Nube o manual) → sólo entregarlo,
                    // NO impacta en las ventas del POS (no genera factura).
                    <button
                      onClick={() => onMarcarEntregado(pedido)}
                      className={
                        "w-full h-6 px-2 rounded-md text-[10px] font-heading font-bold uppercase tracking-wider border transition " +
                        (allDone
                          ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm animate-pulse"
                          : "bg-white text-emerald-700 border-emerald-600/40 hover:bg-emerald-50")
                      }
                      title={allDone
                        ? "Pedido pagado y listo → marcar como entregado"
                        : "Pedido pagado (podés marcar entregado sin cargar al POS)"}
                      data-testid={`mostrador-entregado-${pedido.numero}`}
                    >
                      Entregado
                    </button>
                  )}
                  {esPickup && !pagado && onCargarAlPOS && (
                    <button
                      onClick={() => onCargarAlPOS(pedido)}
                      className={
                        "w-full h-6 px-2 rounded-md text-[10px] font-heading font-bold uppercase tracking-wider border transition " +
                        (allDone
                          ? "bg-[#921A0E] text-white border-[#921A0E] hover:bg-[#7a160b] shadow-sm animate-pulse"
                          : "bg-white text-[#921A0E] border-[#921A0E]/40 hover:bg-[#F0D1B6]/40")
                      }
                      title={allDone ? "Todos los ítems están listos: cargar al POS y cobrar" : "Cargar este pedido al POS para cobrarlo"}
                      data-testid={`mostrador-cargar-pos-${pedido.numero}`}
                    >
                      Cargar al POS
                    </button>
                  )}
                </div>
              );
            })()}
          </td>
        </>
      ) : null}
      <td className={`px-2 py-1 text-right font-mono font-semibold ${isTN ? "text-sky-900" : "text-neutral-800"}`}>
        {qty}
      </td>
      <td className={`px-2 py-1 uppercase ${isTN ? "text-sky-900" : "text-neutral-800"}`}>
        {desc}
      </td>
      <td className="px-2 py-1 text-[12px]">
        {item.especificacion ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-sm bg-amber-100 text-amber-900 font-mono text-[11px] uppercase tracking-wider"
            data-testid={`mostrador-espec-${pedido.numero}-${item.item_idx}`}
          >
            {item.especificacion}
          </span>
        ) : (
          <span className="text-neutral-300">—</span>
        )}
      </td>
      <td className="px-2 py-1">
        <Input
          value={kg}
          onChange={(e) => setKg(e.target.value)}
          onBlur={guardarKg}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          placeholder="0,0"
          inputMode="decimal"
          disabled={saving}
          className={`h-7 text-right font-mono text-[12px] ${tieneSugerencia ? "border-sky-400 bg-sky-50 text-sky-900" : ""}`}
          data-testid={`mostrador-kg-${pedido.numero}-${item.item_idx}`}
        />
        {tieneSugerencia && (
          <div
            className="mt-0.5 text-[9px] font-mono uppercase tracking-wider text-sky-700"
            title={`Sugerido: ${qty} × ${item.tn_weight_kg} kg = ${item.kilos_sugeridos} kg (peso configurado en Tienda Nube)`}
            data-testid={`mostrador-kg-sugerido-${pedido.numero}-${item.item_idx}`}
          >
            ⚖ sug {item.kilos_sugeridos} kg
          </div>
        )}
      </td>
      <td className="px-2 py-1 text-center">
        <button
          disabled={saving}
          onClick={() => toggleEstado("sin_stock")}
          className={`w-full h-7 rounded-md text-[10px] font-heading font-bold uppercase tracking-wider border-2 transition ${
            isSinStock
              ? "bg-red-600 text-white border-red-700 shadow-sm"
              : "bg-white text-red-700 border-red-300 hover:bg-red-50"
          } ${saving ? "opacity-50 cursor-wait" : ""}`}
          data-testid={`mostrador-sinstock-${pedido.numero}-${item.item_idx}`}
        >
          Sin Stock
        </button>
      </td>
      <td className="px-2 py-1 text-center">
        <button
          disabled={saving}
          onClick={() => toggleEstado("terminado")}
          className={`w-full h-7 rounded-md text-[10px] font-heading font-bold uppercase tracking-wider border-2 transition ${
            isTerminado
              ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
              : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          } ${saving ? "opacity-50 cursor-wait" : ""}`}
          data-testid={`mostrador-terminado-${pedido.numero}-${item.item_idx}`}
        >
          Terminado
        </button>
      </td>
    </tr>
  );
}

export function RubroGrid({ rubros, onSelect }) {
  return (
    <div className="grid grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
      {rubros.map((r) => (
        <button
          key={r.id}
          onClick={() => onSelect(r.id)}
          data-testid={`pos-rubro-${r.id}`}
          className="flex flex-col items-center gap-1.5 p-2 rounded-sm hover:bg-neutral-100 transition group"
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-white text-[13px] font-bold shadow-md group-hover:scale-110 transition"
            style={{ backgroundColor: r.color }}
          >
            {r.nombre.substring(0, 3).toUpperCase()}
          </div>
          <span className="text-[10px] text-neutral-700 text-center leading-tight">{r.nombre}</span>
        </button>
      ))}
      {rubros.length === 0 && (
        <div className="col-span-full text-center py-12 text-neutral-400 text-[12px]">
          Sin rubros. Creá algunos desde <strong>Productos y Precios → Rubros</strong>.
        </div>
      )}
    </div>
  );
}

export function ProductGrid({ productos, priceOf, onSelect, onDoubleSelect }) {
  const clickTimers = useRef({});
  const handleClick = (p) => {
    // Si hay un timer pendiente para este producto, es doble-click
    if (clickTimers.current[p.id]) {
      clearTimeout(clickTimers.current[p.id]);
      delete clickTimers.current[p.id];
      if (onDoubleSelect) onDoubleSelect(p);
      return;
    }
    // Programar el "single click" con delay para dar chance al doble-click
    clickTimers.current[p.id] = setTimeout(() => {
      delete clickTimers.current[p.id];
      onSelect(p);
    }, 260);
  };
  return (
    <div className="grid grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
      {productos.map((p) => (
        <button
          key={p.id}
          onClick={() => handleClick(p)}
          data-testid={`pos-producto-${p.id}`}
          title="Click: cargar con kg · Doble-click: reemplaza el último ítem manteniendo los kg"
          className="border border-[#6B1F24]/15 rounded-sm p-2 hover:border-[#921A0E] hover:bg-white transition text-left bg-white group shadow-sm hover:shadow-md"
        >
          <div className="font-mono text-[10px] text-neutral-400">{p.codigo}</div>
          <div className="text-[12px] font-medium leading-tight line-clamp-2 min-h-[30px]">{p.nombre}</div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[11px] text-neutral-500 font-mono uppercase">{p.unidad || "kg"}</span>
            <span className="text-[13px] font-bold text-[#6B1F24]">{fmtARS(priceOf(p))}</span>
          </div>
        </button>
      ))}
      {productos.length === 0 && (
        <div className="col-span-full text-center py-12 text-neutral-400 text-[12px]">
          Sin productos en este rubro.
        </div>
      )}
    </div>
  );
}

export function NumericKeypad({ producto, precio, initialValue, productos = [], priceOf, allowChangeProduct, onChangeProduct, onCancel, onConfirm }) {
  const [valor, setValor] = useState(() => {
    if (initialValue == null) return "";
    // Formato es-AR: "8.5" o "8,5" — mostramos con coma para input humano
    const s = String(initialValue);
    return s === "0" ? "" : s.replace(",", ".");
  });
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const filteredProducts = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return productos.slice(0, 25);
    return productos.filter((p) =>
      (p.nombre || "").toLowerCase().includes(q) ||
      (p.codigo || "").toLowerCase().includes(q)
    ).slice(0, 25);
  }, [productos, pickerSearch]);
  const isKg = (producto.unidad || "kg").toLowerCase() === "kg";

  const press = (k) => {
    if (k === "C") return setValor("");
    if (k === "←") return setValor((v) => v.slice(0, -1));
    if (k === ",") {
      if (!valor.includes(".") && !valor.includes(",")) setValor((v) => (v || "0") + ".");
      return;
    }
    setValor((v) => (v + k).replace(",", "."));
  };

  const numeric = parseFloat(valor || "0");
  const importe = numeric * (precio || 0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter") { if (numeric > 0) onConfirm(numeric); return; }
      if (e.key === "Escape") return onCancel();
      if (/^[0-9]$/.test(e.key)) press(e.key);
      if (e.key === "." || e.key === ",") press(",");
      if (e.key === "Backspace") press("←");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [valor]);

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-sm" data-testid="numeric-keypad">
        <DialogHeader>
          <DialogTitle className="text-[15px] flex items-center justify-between gap-2">
            <span className="truncate">{producto.nombre}</span>
            {allowChangeProduct && (
              <button
                onClick={() => setShowPicker((v) => !v)}
                data-testid="keypad-change-product"
                className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-[#F0D1B6]/50 hover:bg-[#F0D1B6] text-[#6B1F24] font-semibold flex items-center gap-1 whitespace-nowrap"
              >
                {showPicker ? "Cerrar" : "Cambiar producto"}
              </button>
            )}
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {isKg ? "Ingresá los kilos" : "Ingresá la cantidad"} · Precio {fmtARS(precio)}/{producto.unidad || "kg"}
          </DialogDescription>
        </DialogHeader>

        {showPicker && (
          <div className="border border-[#6B1F24]/20 rounded-sm bg-white p-2 -mt-1">
            <Input
              autoFocus
              data-testid="keypad-product-search"
              placeholder="Buscar producto por nombre o código..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              className="h-8 text-[12px] border-[#6B1F24]/20 mb-1"
            />
            <div className="max-h-40 overflow-y-auto">
              {filteredProducts.map((p) => (
                <button
                  key={p.id}
                  data-testid={`keypad-picker-option-${p.id}`}
                  onClick={() => {
                    if (onChangeProduct) onChangeProduct(p);
                    setShowPicker(false);
                    setPickerSearch("");
                  }}
                  className="w-full text-left px-2 py-1 hover:bg-[#F0D1B6]/30 border-b border-neutral-100 text-[12px] flex justify-between items-center"
                >
                  <span className="truncate">
                    <span className="font-mono text-[10px] text-neutral-500 mr-1.5">{p.codigo}</span>
                    {p.nombre}
                  </span>
                  <span className="font-mono text-[11px] font-bold text-[#6B1F24]">
                    {fmtARS(priceOf ? priceOf(p) : p.precio_actual)}
                  </span>
                </button>
              ))}
              {filteredProducts.length === 0 && (
                <div className="text-center py-3 text-neutral-400 text-[11px]">Sin resultados</div>
              )}
            </div>
          </div>
        )}
        <div className="bg-neutral-100 rounded-sm p-3 text-right">
          <div className="text-[36px] font-mono font-bold" data-testid="keypad-display">
            {valor || "0"} <span className="text-[14px] text-neutral-500">{producto.unidad || "kg"}</span>
          </div>
          <div className="text-[16px] font-bold text-[#6B1F24] mt-1" data-testid="keypad-importe">
            = {fmtARS(importe)}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "←"].map((k) => (
            <button
              key={k}
              data-testid={`keypad-${k}`}
              onClick={() => press(k)}
              className="h-14 rounded-sm border border-neutral-300 bg-white hover:bg-neutral-50 text-[20px] font-mono font-bold active:scale-95 transition"
            >
              {k}
            </button>
          ))}
          <button
            data-testid="keypad-C"
            onClick={() => press("C")}
            className="col-span-1 h-12 rounded-sm bg-neutral-200 hover:bg-neutral-300 text-[14px] font-bold"
          >
            C
          </button>
          <button
            data-testid="keypad-ok"
            onClick={() => numeric > 0 && onConfirm(numeric)}
            disabled={!numeric}
            className="col-span-2 h-12 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-heading uppercase tracking-widest disabled:opacity-40"
          >
            Aceptar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentDialog({ mode, total, cajas, mediosPago, cliente, processing, onCancel, onConfirm }) {
  // Si hay medios de pago configurados (Etapa 2), usarlos. Sino, fallback al
  // filtrado histórico por nombre de caja.
  const usesMedios = Array.isArray(mediosPago) && mediosPago.length > 0;

  // Cuenta corriente: sólo se habilita si el cliente NO es Consumidor Final (necesita saldo trazable).
  const clienteEsCF = !cliente
    || (cliente.nombre || "").toLowerCase().trim() === "consumidor final"
    || cliente.tipo_documento === "CF";
  const [useCtaCte, setUseCtaCte] = useState(false);

  // Opciones: cada una tiene {id, nombre, caja_id, caja_nombre, tipo}
  const opciones = useMemo(() => {
    if (usesMedios) {
      return mediosPago
        .filter((m) => m.caja_id && m.caja_tipo !== "cheques")
        .map((m) => ({
          id: `mp_${m.id}`,        // id de UI (medio de pago)
          medio_pago_id: m.id,
          nombre: m.nombre,
          caja_id: m.caja_id,
          caja_nombre: m.caja_nombre,
          tipo: m.tipo,
        }));
    }
    // Fallback: filtrar por TIPO (fuente única de verdad); si la caja no trae tipo,
    // caemos por nombre. Esto permite renombrar las cajas libremente (ej: "Caja
    // Mostrador 1") sin romper el checkout mientras el `tipo` esté seteado en Admin.
    const enriched = cajas.map((c) => ({
      ...c,
      _tipo: String(c.tipo || "").toLowerCase(),
      _n: String(c.nombre || "").toLowerCase(),
    }));
    const matchEfectivo = (c) => c._tipo === "efectivo" || (!c._tipo && c._n.includes("efectivo"));
    const matchQR = (c) => c._tipo === "qr" || (!c._tipo && (c._n.includes("qr") || c._n.includes("max")));
    const matchMP = (c) => c._tipo === "mercado_pago" || (!c._tipo && c._n.includes("mercado"));
    const matchPayway = (c) => c._tipo === "debito" || c._tipo === "credito" || (!c._tipo && c._n.includes("payway"));
    const filtradas = mode === "cobrar"
      ? enriched.filter((c) => matchEfectivo(c) || matchQR(c))
      : enriched.filter((c) => matchEfectivo(c) || matchMP(c) || matchPayway(c));
    return filtradas.map((c) => ({
      id: c.id,
      medio_pago_id: null,
      nombre: c.nombre,
      caja_id: c.id,
      caja_nombre: c.nombre,
      tipo: c.tipo,
    }));
  }, [cajas, mediosPago, mode, usesMedios]);

  // Estado: montos por caja_id — todas las cajas listadas, monto vacío por defecto
  const [montos, setMontos] = useState({});

  const totalPagos = Object.values(montos).reduce(
    (s, v) => s + (parseFloat(v) || 0),
    0
  );
  const diff = total - totalPagos;
  const restante = Math.max(0, diff);

  // Detectar medios de pago en efectivo (para permitir "vuelto")
  const efectivoIds = useMemo(() => {
    const set = new Set();
    for (const op of opciones) {
      const t = (op.tipo || "").toLowerCase();
      const nl = (op.nombre || "").toLowerCase();
      if (t === "efectivo" || nl.includes("efectivo") || nl.includes("efvo")) {
        set.add(op.id);
      }
    }
    return set;
  }, [opciones]);

  const efectivoTotal = Object.entries(montos).reduce(
    (s, [k, v]) => (efectivoIds.has(k) ? s + (parseFloat(v) || 0) : s),
    0
  );
  const noEfectivoTotal = totalPagos - efectivoTotal;
  const exceso = totalPagos - total;
  // Se acepta "vuelto" sólo si el exceso proviene de efectivo y los medios no-efectivo no exceden el total
  const vuelto = (exceso > 0.01 && noEfectivoTotal <= total + 0.01 && efectivoTotal >= exceso - 0.01)
    ? exceso
    : 0;
  const pagoValido = Math.abs(diff) < 0.01 || vuelto > 0;

  const setMonto = (cid, val) => setMontos((m) => ({ ...m, [cid]: val }));

  // Al clickear el tilde: setea el monto = restante del total menos las OTRAS cajas
  const autofillCaja = (cid) => {
    setMontos((m) => {
      const otrasSuma = Object.entries(m).reduce(
        (s, [k, v]) => (k === cid ? s : s + (parseFloat(v) || 0)),
        0
      );
      const falta = Math.max(0, total - otrasSuma);
      return { ...m, [cid]: falta.toFixed(2) };
    });
  };

  const cajaIcon = (item) => {
    // Prefer tipo del medio de pago si viene, sino inferir del nombre.
    const t = (item.tipo || "").toLowerCase();
    if (t === "efectivo") return Money;
    if (t === "mercado_pago") return DeviceMobile;
    if (t === "qr") return QrCode;
    if (t === "debito" || t === "credito") return CreditCard;
    if (t === "transferencia") return Bank;
    const nl = (item.nombre || "").toLowerCase();
    if (nl.includes("efectivo")) return Money;
    if (nl.includes("mercado")) return DeviceMobile;
    if (nl.includes("qr") || nl.includes("max")) return QrCode;
    if (nl.includes("payway") || nl.includes("posnet") || nl.includes("tarjeta")) return CreditCard;
    return Wallet;
  };

  const handleConfirm = () => {
    // Si el operador marcó "Cuenta Corriente", enviamos un pago especial
    // con caja_id="cta_cte" para que POSPage.submitVenta NO genere cobros
    // y la factura quede pendiente sumando al saldo del cliente.
    if (useCtaCte) {
      onConfirm(
        [{ caja_id: "cta_cte", monto: total.toFixed(2) }],
        { vuelto: 0, metodo_pago: "Cuenta Corriente" }
      );
      return;
    }
    // Consolidar por caja_id (si dos medios apuntan a la misma caja).
    // Si hay vuelto, reducir proporcionalmente los pagos en efectivo
    // para que la suma registrada sea igual al total de la venta.
    const acc = {};
    const cashNeeded = Math.max(0, total - noEfectivoTotal);
    const efectivoBase = efectivoTotal;
    const medioNombres = [];
    for (const op of opciones) {
      let monto = parseFloat(montos[op.id] || 0);
      if (monto <= 0) continue;
      medioNombres.push(op.nombre);
      if (vuelto > 0.01 && efectivoIds.has(op.id) && efectivoBase > 0) {
        // Distribuir cashNeeded proporcionalmente entre los medios en efectivo
        monto = (monto / efectivoBase) * cashNeeded;
      }
      if (monto <= 0) continue;
      const key = op.caja_id;
      acc[key] = (acc[key] || 0) + monto;
    }
    const pagos = Object.entries(acc).map(([caja_id, monto]) => ({
      caja_id, monto: monto.toFixed(2),
    }));
    onConfirm(pagos, {
      vuelto: vuelto > 0.01 ? Math.round(vuelto * 100) / 100 : 0,
      metodo_pago: medioNombres.length ? [...new Set(medioNombres)].join(" · ") : "",
    });
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24] font-heading">
            {mode === "cobrar" ? <CashRegister size={20} /> : <Receipt size={20} />}
            {mode === "cobrar" ? "Cobrar" : "Facturar y Cobrar"}
          </DialogTitle>
          <DialogDescription>
            {mode === "cobrar"
              ? "Se emite un Ticket X no fiscal + registra el cobro."
              : "Se emite Factura B/A con CAE de AFIP + registra el cobro."}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[#F0D1B6]/30 border border-[#6B1F24]/15 rounded p-3 text-center">
          <div className="text-[11px] uppercase font-mono text-neutral-500">Total a cobrar</div>
          <div className="text-[32px] font-bold font-mono text-[#6B1F24]">{fmtARS(total)}</div>
          {!useCtaCte && restante > 0.01 && (
            <div className="text-[11px] font-mono text-amber-700 mt-1">
              Restan {fmtARS(restante)}
            </div>
          )}
        </div>

        {/* Toggle Cuenta Corriente — solo para clientes identificados (no Consumidor Final) */}
        {!clienteEsCF && (
          <button
            type="button"
            onClick={() => setUseCtaCte((v) => !v)}
            data-testid="pos-toggle-ctacte"
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded border-2 text-left transition ${
              useCtaCte
                ? "border-amber-500 bg-amber-50"
                : "border-dashed border-neutral-300 bg-white hover:bg-neutral-50 hover:border-neutral-400"
            }`}
          >
            <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
              useCtaCte ? "bg-amber-500 border-amber-500" : "border-neutral-400 bg-white"
            }`}>
              {useCtaCte && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </span>
            <div className="flex-1">
              <div className="text-[13px] font-heading font-bold uppercase tracking-wide text-amber-900">
                Cuenta Corriente
              </div>
              <div className="text-[11px] text-neutral-600">
                La factura queda pendiente y suma al saldo de <strong>{cliente?.nombre}</strong>.
              </div>
            </div>
          </button>
        )}

        {opciones.length === 0 ? (
          <div className="border border-amber-300 bg-amber-50 rounded p-3 text-[12px] text-amber-800">
            No hay medios de pago configurados. Andá a <strong>Administración → Medios de Pago</strong> y creá al menos:
            {mode === "cobrar" ? " Efectivo · QR MAX" : " Efectivo · Mercado Pago · Tarjeta"}
          </div>
        ) : (
          <div className={`space-y-1.5 ${useCtaCte ? "opacity-40 pointer-events-none" : ""}`} data-testid="pagos-lista">
            {opciones.map((c, i) => {
              const Icon = cajaIcon(c);
              const val = montos[c.id] || "";
              const activa = parseFloat(val) > 0;
              return (
                <div
                  key={c.id}
                  className={`grid grid-cols-[1fr_140px_40px] gap-2 items-center rounded border p-2 transition ${
                    activa
                      ? "border-[#4DC247] bg-[#4DC247]/5"
                      : "border-neutral-200 bg-white hover:border-[#6B1F24]/30"
                  }`}
                  data-testid={`pago-row-${i}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`h-8 w-8 rounded flex items-center justify-center flex-shrink-0 ${
                        activa ? "bg-[#4DC247]/15 text-[#4DC247]" : "bg-neutral-100 text-neutral-500"
                      }`}
                    >
                      <Icon size={16} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-neutral-800 truncate" data-testid={`pago-caja-nombre-${i}`}>
                        {c.nombre}
                      </div>
                      <div className="text-[10px] font-mono text-neutral-400 uppercase">
                        {c.tipo || "caja"}
                        {c.caja_nombre && c.caja_nombre !== c.nombre && (
                          <span className="ml-1 text-neutral-500">→ {c.caja_nombre}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <MoneyInput
                    step="0.01"
                    min="0"
                    data-testid={`pago-monto-${c.id}`}
                    value={val}
                    onChange={(e) => setMonto(c.id, e.target.value)}
                    placeholder="0,00"
                    className="h-9 font-mono text-right text-[14px] border-neutral-300"
                  />
                  <button
                    type="button"
                    onClick={() => autofillCaja(c.id)}
                    title="Completar con el monto restante"
                    data-testid={`pago-tilde-${c.id}`}
                    className="h-9 w-9 rounded flex items-center justify-center bg-[#4DC247] hover:bg-[#3fa63a] text-white transition active:scale-95"
                  >
                    <Check size={16} weight="bold" />
                  </button>
                </div>
              );
            })}

            <div className="border-t pt-2 mt-2 text-[13px] flex justify-between">
              <span className="text-neutral-600">Suma pagos:</span>
              <span className="font-mono font-bold" data-testid="pagos-suma">{fmtARS(totalPagos)}</span>
            </div>
            {Math.abs(diff) > 0.01 && !useCtaCte && (
              <div
                className={`text-[12px] text-center py-1 rounded ${
                  diff > 0
                    ? "bg-amber-50 text-amber-700"
                    : vuelto > 0
                    ? "bg-emerald-50 text-emerald-700 font-semibold"
                    : "bg-red-50 text-red-700"
                }`}
                data-testid="pago-diff-msg"
              >
                {diff > 0
                  ? `Falta cobrar ${fmtARS(diff)}`
                  : vuelto > 0
                  ? `Vuelto ${fmtARS(vuelto)}`
                  : `Excede en ${fmtARS(Math.abs(diff))}`}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={processing}>Cancelar</Button>
          <Button
            data-testid="btn-confirmar-pago"
            onClick={handleConfirm}
            disabled={processing || (!useCtaCte && (opciones.length === 0 || !pagoValido || totalPagos <= 0))}
            className={mode === "cobrar" ? "bg-[#6B1F24] hover:bg-[#8F191C]" : "bg-[#4DC247] hover:bg-[#3fa63a]"}
          >
            {processing ? "Procesando..." : (mode === "cobrar" ? "Cobrar" : "Facturar y Cobrar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CLIENTE RÁPIDO — Alta express con auto-búsqueda en Padrón AFIP
// ============================================================================

export function QuickClienteDialog({ onCancel, onCreated }) {
  const [documento, setDocumento] = useState("");
  const [nombre, setNombre] = useState("");
  const [condicionIva, setCondicionIva] = useState("Consumidor Final");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [padronLoading, setPadronLoading] = useState(false);

  const isCuit = (documento || "").replace(/\D/g, "").length === 11;

  const buscarPadron = async () => {
    const doc = (documento || "").replace(/\D/g, "");
    if (doc.length !== 11) {
      toast.error("Ingresá un CUIT válido de 11 dígitos");
      return;
    }
    setPadronLoading(true);
    try {
      const { data } = await api.get(`/afip/padron/${doc}`);
      setNombre(data.razon_social || data.nombre || nombre);
      const cond = data.condicion_iva || data.categoria || "";
      const condLower = cond.toLowerCase();
      if (condLower.includes("responsable inscripto") || condLower.includes("ri")) setCondicionIva("Responsable Inscripto");
      else if (condLower.includes("monotributista") || condLower.includes("monotributo")) setCondicionIva("Monotributista");
      else if (condLower.includes("exento")) setCondicionIva("Exento");
      else if (condLower.includes("consumidor final")) setCondicionIva("Consumidor Final");
      toast.success("Datos traídos de Padrón AFIP");
    } catch (err) {
      toast.error("No se pudo obtener del Padrón. Completá manualmente.");
    } finally {
      setPadronLoading(false);
    }
  };

  const guardar = async () => {
    if (!nombre.trim()) return toast.error("Nombre obligatorio");
    setSaving(true);
    try {
      const isCuitDoc = (documento || "").replace(/\D/g, "").length === 11;
      const { data } = await api.post("/clientes", {
        tipo: "cliente",
        nombre: nombre.trim(),
        cuit: isCuitDoc ? documento.replace(/\D/g, "") : null,
        dni: !isCuitDoc && documento ? documento.replace(/\D/g, "") : null,
        condicion_iva: condicionIva,
        email: email || null,
      });
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear el cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="quick-cliente-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24] font-heading">
            <UserPlus size={20} weight="bold" /> Cliente Rápido
          </DialogTitle>
          <DialogDescription>
            Ideal para clientes que sólo compran ocasionalmente y necesitan Factura A/B.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">CUIT / DNI</Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="quick-cliente-documento"
                autoFocus
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                placeholder="20-12345678-9 o 12345678"
                className="font-mono border-[#6B1F24]/20"
              />
              <Button
                data-testid="btn-buscar-padron"
                onClick={buscarPadron}
                disabled={!isCuit || padronLoading}
                className="bg-[#6B1F24] hover:bg-[#8F191C] whitespace-nowrap"
              >
                {padronLoading ? "..." : "Buscar en AFIP"}
              </Button>
            </div>
            {!isCuit && documento && (
              <div className="text-[10px] text-neutral-500 mt-0.5">
                Ingresá 11 dígitos para buscar en Padrón AFIP.
              </div>
            )}
          </div>

          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">Nombre / Razón Social *</Label>
            <Input
              data-testid="quick-cliente-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Juan Pérez / La Empresa SA"
              className="mt-1 border-[#6B1F24]/20"
            />
          </div>

          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">Condición IVA</Label>
            <Select value={condicionIva} onValueChange={setCondicionIva}>
              <SelectTrigger data-testid="quick-cliente-cond" className="mt-1 border-[#6B1F24]/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Consumidor Final">Consumidor Final</SelectItem>
                <SelectItem value="Responsable Inscripto">Responsable Inscripto</SelectItem>
                <SelectItem value="Monotributista">Monotributista</SelectItem>
                <SelectItem value="Exento">Exento</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">E-mail (opcional)</Label>
            <Input
              data-testid="quick-cliente-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@ejemplo.com"
              className="mt-1 border-[#6B1F24]/20"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
          <Button
            data-testid="btn-guardar-cliente-rapido"
            onClick={guardar}
            disabled={saving || !nombre.trim()}
            className="bg-[#4DC247] hover:bg-[#3fa63a]"
          >
            {saving ? "Guardando..." : "Crear y seleccionar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// CIERRE DE CAJA X/Z — Reporte final del turno
// ============================================================================

export function CierreCajaDialog({ onClose }) {
  const [fecha, setFecha] = useState(() => new Date().toISOString().substring(0, 10));
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmZ, setConfirmZ] = useState(false);

  const cargarReporte = useCallback(async (m = "X") => {
    setLoading(true);
    try {
      const { data } = await api.post("/pos/cierre-caja", { fecha, modo: m });
      setReporte(data);
      if (m === "Z") toast.success("Cierre Z registrado. Turno cerrado.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  }, [fecha]);

  useEffect(() => { cargarReporte("X"); }, [cargarReporte]);

  const confirmarZ = async () => {
    if (!confirmZ) return setConfirmZ(true);
    setConfirmZ(false);
    await cargarReporte("Z");
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cierre-caja-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24] font-heading">
            <ClockCounterClockwise size={22} weight="bold" /> Cierre de Caja {reporte?.modo || "X"}
          </DialogTitle>
          <DialogDescription>
            {reporte?.modo === "Z"
              ? "Cierre definitivo del turno (Z). Registrado en el historial."
              : "Reporte informativo (X). No cierra el turno hasta que confirmes con Cierre Z."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 mb-4">
          <Label className="text-[12px] font-medium">Fecha</Label>
          <DateInput
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="max-w-[160px] h-9 border-[#6B1F24]/20"
            data-testid="cierre-fecha"
          />
          <Button
            size="sm"
            onClick={() => cargarReporte("X")}
            disabled={loading}
            variant="outline"
            className="border-[#6B1F24]/30 text-[#6B1F24]"
            data-testid="btn-refresh-cierre"
          >
            Actualizar
          </Button>
        </div>

        {loading && <div className="text-center py-8 text-neutral-500">Generando...</div>}

        {reporte && !loading && (
          <div className="space-y-4 print:text-black">
            <div className="text-center border-y border-[#6B1F24]/20 py-3">
              <div className="font-heading text-[16px] font-bold text-[#6B1F24]">LA BIFERIA</div>
              <div className="text-[11px] uppercase font-mono tracking-widest text-neutral-600">
                Cierre {reporte.modo} — {reporte.fecha}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">
                Operador: {reporte.operador} · Generado: {new Date(reporte.generado_at).toLocaleString("es-AR")}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase font-mono font-bold text-[#921A0E] mb-1">Ventas por tipo</div>
              <div className="border border-[#6B1F24]/20 rounded overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="bg-[#F0D1B6]/30">
                      <tr className="text-[10px] uppercase font-mono text-neutral-700">
                        <th className="text-left px-2 py-1.5">Tipo</th>
                        <th className="text-right px-2 py-1.5 w-20">Cant.</th>
                        <th className="text-right px-2 py-1.5 w-20">c/CAE</th>
                        <th className="text-right px-2 py-1.5 w-28">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reporte.ventas.por_tipo.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-4 text-neutral-400">Sin ventas hoy</td></tr>
                      )}
                      {reporte.ventas.por_tipo.map((v) => (
                        <tr key={v.tipo_comprobante} className="border-t border-[#6B1F24]/10">
                          <td className="px-2 py-1 font-medium">{v.tipo_comprobante}</td>
                          <td className="text-right px-2 py-1 font-mono">{v.cantidad}</td>
                          <td className="text-right px-2 py-1 font-mono text-emerald-700">{v.con_cae || "—"}</td>
                          <td className="text-right px-2 py-1 font-mono font-bold">{fmtARS(v.monto)}</td>
                        </tr>
                      ))}
                      <tr className="bg-[#F0D1B6]/20 border-t-2 border-[#6B1F24]/30">
                        <td className="px-2 py-1.5 font-heading font-bold uppercase text-[#6B1F24]">Total ventas</td>
                        <td className="text-right px-2 py-1.5 font-mono font-bold">{reporte.ventas.total_comprobantes}</td>
                        <td></td>
                        <td className="text-right px-2 py-1.5 font-mono font-bold text-[#6B1F24]">{fmtARS(reporte.ventas.total_monto)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase font-mono font-bold text-[#921A0E] mb-1">Ingresos por caja</div>
              <div className="border border-[#6B1F24]/20 rounded overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="bg-[#F0D1B6]/30">
                      <tr className="text-[10px] uppercase font-mono text-neutral-700">
                        <th className="text-left px-2 py-1.5">Caja</th>
                        <th className="text-left px-2 py-1.5 w-24">Tipo</th>
                        <th className="text-right px-2 py-1.5 w-20">Mov.</th>
                        <th className="text-right px-2 py-1.5 w-28">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reporte.ingresos.por_caja.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-4 text-neutral-400">Sin movimientos hoy</td></tr>
                      )}
                      {reporte.ingresos.por_caja.map((c) => (
                        <tr key={c.caja_id} className="border-t border-[#6B1F24]/10">
                          <td className="px-2 py-1 font-medium">{c.caja_nombre}</td>
                          <td className="px-2 py-1 text-[10px] uppercase text-neutral-500">{c.caja_tipo}</td>
                          <td className="text-right px-2 py-1 font-mono">{c.cantidad}</td>
                          <td className="text-right px-2 py-1 font-mono font-bold">{fmtARS(c.monto)}</td>
                        </tr>
                      ))}
                      <tr className="bg-[#4DC247]/10 border-t-2 border-[#4DC247]/40">
                        <td colSpan={2} className="px-2 py-1.5 font-heading font-bold uppercase text-[#6B1F24]">Total ingresado</td>
                        <td></td>
                        <td className="text-right px-2 py-1.5 font-mono font-bold text-[#4DC247]">{fmtARS(reporte.ingresos.total_monto)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {Math.abs(reporte.diferencia_ventas_ingresos) > 0.01 && (
              <div className={`p-2 rounded text-[12px] font-mono text-center ${
                reporte.diferencia_ventas_ingresos > 0 ? "bg-amber-50 text-amber-800 border border-amber-300" : "bg-red-50 text-red-800 border border-red-300"
              }`}>
                <strong>Diferencia Ventas vs Ingresos:</strong> {fmtARS(reporte.diferencia_ventas_ingresos)}
                <div className="text-[10px] mt-0.5 opacity-70">
                  {reporte.diferencia_ventas_ingresos > 0
                    ? "Hay ventas sin cobrar (cuenta corriente) o cobros no registrados."
                    : "Hay ingresos que no corresponden a ventas del día."}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="print:hidden">Cerrar</Button>
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="gap-2 print:hidden border-[#6B1F24]/30 text-[#6B1F24]"
            disabled={!reporte}
            data-testid="btn-imprimir-cierre"
          >
            <Printer size={14} /> Imprimir
          </Button>
          {reporte?.modo !== "Z" && (
            <Button
              onClick={confirmarZ}
              disabled={loading || !reporte}
              className="bg-[#6B1F24] hover:bg-[#8F191C] gap-2 print:hidden"
              data-testid="btn-cerrar-z"
            >
              <FileText size={14} />
              {confirmZ ? "Confirmar Cierre Z" : "Cierre Z (turno)"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================================
// CAJA DIARIA — Vista de la sesión de caja (Datos + Ingresos por venta + más)
// ============================================================================

export function CajaDiariaView({ sesion, sesionLoading, onReload, onCierreClick }) {
  const [subtab, setSubtab] = useState("datos");
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);

  const loadVentas = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pos/ventas-dia");
      setVentas(data || []);
    } catch (err) {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVentas(); }, [loadVentas]);

  // Excluye NC y anuladas para no restar/duplicar el ingreso neto del día.
  const esNotaCredito = (v) => {
    const t = (v.tipo_comprobante || "").toUpperCase();
    return t.includes("NOTA") && (t.includes("CREDITO") || t.includes("CRÉDITO"));
  };
  const ventasEfectivas = useMemo(
    () => ventas.filter((v) => v.estado !== "anulada" && !esNotaCredito(v)),
    [ventas]
  );

  const totalIngresos = useMemo(
    () => ventasEfectivas.reduce((s, v) => s + (v.total || 0), 0),
    [ventasEfectivas]
  );
  const totalConCae = useMemo(() => ventasEfectivas.filter((v) => v.cae).length, [ventasEfectivas]);
  const totalSinCae = useMemo(() => ventasEfectivas.filter((v) => !v.cae).length, [ventasEfectivas]);
  const totalAnuladas = useMemo(() => ventas.filter((v) => v.estado === "anulada").length, [ventas]);

  // Filtros de la vista Ingresos por venta
  const [filterFiscal, setFilterFiscal] = useState("todos"); // todos | fiscales | no_fiscales
  const [filterCliente, setFilterCliente] = useState("");
  const [filterMedio, setFilterMedio] = useState("todos");

  const mediosDisponibles = useMemo(() => {
    const set = new Map();
    for (const v of ventas) {
      for (const m of (v.medios_pago || [])) {
        if (m.nombre && !set.has(m.nombre)) set.set(m.nombre, m);
      }
    }
    return Array.from(set.values());
  }, [ventas]);

  const filteredVentas = useMemo(() => {
    const q = filterCliente.trim().toLowerCase();
    return ventas.filter((v) => {
      if (filterFiscal === "fiscales" && !v.cae) return false;
      if (filterFiscal === "no_fiscales" && !!v.cae) return false;
      if (q && !(v.contraparte_nombre || "").toLowerCase().includes(q)) return false;
      if (filterMedio !== "todos") {
        const nombres = (v.medios_pago || []).map((m) => m.nombre);
        if (!nombres.includes(filterMedio)) return false;
      }
      return true;
    });
  }, [ventas, filterFiscal, filterCliente, filterMedio]);

  const totalFiltrado = useMemo(
    () => filteredVentas.filter((v) => v.estado !== "anulada" && !esNotaCredito(v)).reduce((s, v) => s + (v.total || 0), 0),
    [filteredVentas]
  );
  const filtrosActivos = filterFiscal !== "todos" || filterCliente.trim() !== "" || filterMedio !== "todos";

  const fmtHora = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="flex-1 flex bg-[#F9F6F6] overflow-hidden">
      {/* Sidebar de subtabs */}
      <aside className="w-52 bg-white border-r border-[#6B1F24]/20 flex-shrink-0 flex flex-col">
        <div className="px-3 py-2.5 border-b border-[#6B1F24]/10">
          <div className="font-heading font-bold text-[13px] text-[#6B1F24] flex items-center gap-1.5">
            <ClockCounterClockwise size={16} weight="bold" /> Caja Diaria
          </div>
          <div className={`text-[10px] font-mono mt-0.5 ${sesion?.active ? "text-emerald-600" : "text-red-600"}`}>
            {sesion?.active ? "● ABIERTA" : "○ CERRADA"}
          </div>
        </div>
        {[
          { key: "datos",       label: "Datos",              icon: User },
          { key: "ingresos",    label: "Ingresos por venta", icon: Receipt },
          { key: "arqueo",      label: "Arqueo de efectivo", icon: Coin },
          { key: "ajuste",      label: "Ajuste",             icon: Scales },
          { key: "alivio",      label: "Alivio de caja",     icon: ArrowLeft },
        ].map((tab) => {
          const Icon = tab.icon;
          const active = subtab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setSubtab(tab.key)}
              data-testid={`caja-subtab-${tab.key}`}
              className={
                active
                  ? "px-3 py-2 text-[12px] font-medium border-l-4 border-[#921A0E] bg-[#F0D1B6]/30 text-[#6B1F24] flex items-center gap-2"
                  : "px-3 py-2 text-[12px] text-neutral-600 hover:bg-neutral-50 border-l-4 border-transparent flex items-center gap-2"
              }
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </aside>

      {/* Contenido */}
      <main className="flex-1 overflow-auto p-5">
        {sesionLoading && <div className="text-center py-16 text-neutral-500">Cargando...</div>}

        {!sesionLoading && subtab === "datos" && (
          <div className="max-w-3xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-[22px] font-bold text-[#6B1F24]">Caja actual</h2>
                <p className="text-[12px] text-neutral-600">
                  Estado de la sesión de caja. {sesion?.active
                    ? "La caja está abierta y podés operar en el POS."
                    : "La caja está cerrada. Debés abrirla para poder vender."}
                </p>
              </div>
              {sesion?.active ? (
                <Button
                  data-testid="btn-cerrar-caja"
                  onClick={() => setCloseDialog(true)}
                  className="bg-red-600 hover:bg-red-700 gap-2"
                >
                  <LinkSimpleBreak size={14} /> Cerrar Caja
                </Button>
              ) : (
                <Button
                  data-testid="btn-abrir-caja"
                  onClick={() => setOpenDialog(true)}
                  className="bg-[#4DC247] hover:bg-[#3fa63a] gap-2"
                >
                  <LinkSimple size={14} /> Abrir Caja
                </Button>
              )}
            </div>

            {!sesion?.active && (
              <div className="mb-4 border border-amber-300 bg-amber-50 rounded p-3 text-[12px] text-amber-800" data-testid="caja-cerrada-banner">
                🔒 <strong>La caja está cerrada.</strong> Los botones "Cobrar" y "Facturar y Cobrar" del POS están deshabilitados hasta que abras la caja.
              </div>
            )}

            {sesion?.active && (
              <div className="grid grid-cols-2 gap-3">
                <FieldCard label="Operador que abrió" value={sesion.operador?.username || "—"} icon={User} testid="caja-operador" />
                <FieldCard label="Email" value={sesion.operador?.email || "—"} mono testid="caja-email" />
                <FieldCard label="Apertura" value={fmtHora(sesion.apertura_at)} mono testid="caja-apertura" />
                <FieldCard label="Saldo inicial" value={fmtARS(sesion.saldo_inicial || 0)} mono testid="caja-saldo-inicial" />
                {sesion.notas_apertura && (
                  <div className="col-span-2 text-[11px] text-neutral-500 border-l-2 border-[#F0D1B6] pl-2 italic">
                    "{sesion.notas_apertura}"
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!sesionLoading && subtab === "ingresos" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-[20px] font-bold text-[#6B1F24]">Ingresos por venta</h2>
                <p className="text-[12px] text-neutral-600">Todas las ventas del día {sesion?.fecha || ""}</p>
              </div>
              <Button
                size="sm"
                onClick={loadVentas}
                variant="outline"
                className="gap-1 border-[#6B1F24]/30 text-[#6B1F24]"
                data-testid="btn-refresh-ingresos"
              >
                Actualizar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <StatBox label="Ventas del día" value={ventasEfectivas.length} testid="stat-ventas" />
              <StatBox label="Con CAE (fiscales)" value={totalConCae} color="text-emerald-700" testid="stat-cae" />
              <StatBox label="Sin CAE" value={totalSinCae} color="text-amber-700" testid="stat-sin-cae" />
              <StatBox label="Total facturado" value={fmtARS(totalIngresos)} color="text-[#6B1F24]" wide mono testid="stat-total" />
            </div>

            {/* Filtros: Fiscal / Cliente / Medio de pago */}
            <div className="border border-[#6B1F24]/20 rounded bg-white p-3 mb-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <div className="text-[10px] uppercase font-mono text-neutral-500 mb-1">Fiscal</div>
                  <div className="inline-flex rounded overflow-hidden border border-[#6B1F24]/30" data-testid="filtro-fiscal-group">
                    {[
                      { k: "todos", label: "Todos" },
                      { k: "fiscales", label: "Fiscales" },
                      { k: "no_fiscales", label: "No fiscales" },
                    ].map((opt) => (
                      <button
                        key={opt.k}
                        type="button"
                        data-testid={`filtro-fiscal-${opt.k}`}
                        onClick={() => setFilterFiscal(opt.k)}
                        className={`px-3 h-8 text-[12px] font-medium transition ${filterFiscal === opt.k
                          ? "bg-[#6B1F24] text-white"
                          : "bg-white text-[#6B1F24] hover:bg-[#F0D1B6]/30"}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase font-mono text-neutral-500 mb-1">Cliente</div>
                  <Input
                    data-testid="filtro-cliente"
                    value={filterCliente}
                    onChange={(e) => setFilterCliente(e.target.value)}
                    placeholder="Buscar por nombre…"
                    className="h-8 text-[12px]"
                  />
                </div>
                <div className="min-w-[180px]">
                  <div className="text-[10px] uppercase font-mono text-neutral-500 mb-1">Medio de pago</div>
                  <select
                    data-testid="filtro-medio-pago"
                    value={filterMedio}
                    onChange={(e) => setFilterMedio(e.target.value)}
                    className="h-8 text-[12px] rounded border border-[#6B1F24]/30 bg-white px-2 w-full"
                  >
                    <option value="todos">Todos</option>
                    {mediosDisponibles.map((m) => (
                      <option key={m.nombre} value={m.nombre}>{m.nombre}</option>
                    ))}
                  </select>
                </div>
                {filtrosActivos && (
                  <button
                    type="button"
                    data-testid="filtro-limpiar"
                    onClick={() => { setFilterFiscal("todos"); setFilterCliente(""); setFilterMedio("todos"); }}
                    className="h-8 px-3 text-[11px] text-neutral-600 hover:text-[#6B1F24] underline underline-offset-4"
                  >
                    Limpiar filtros
                  </button>
                )}
                {filtrosActivos && (
                  <div className="ml-auto text-[11px] font-mono text-neutral-600" data-testid="filtro-resultado">
                    <span className="text-[#6B1F24] font-bold">{filteredVentas.length}</span> de {ventas.length} · Total filtrado: <span className="font-bold text-[#6B1F24]">{fmtARS(totalFiltrado)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border border-[#6B1F24]/20 rounded overflow-hidden bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-[#F0D1B6]/30 text-[10px] uppercase font-mono text-neutral-700 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5 w-24">Hora</th>
                      <th className="text-left px-3 py-1.5 w-32">Comprobante</th>
                      <th className="text-left px-3 py-1.5 w-24">Nº</th>
                      <th className="text-left px-3 py-1.5">Cliente</th>
                      <th className="text-left px-3 py-1.5 w-32">Pago</th>
                      <th className="text-right px-3 py-1.5 w-16">Ítems</th>
                      <th className="text-right px-3 py-1.5 w-16">CAE</th>
                      <th className="text-right px-3 py-1.5 w-28">Total</th>
                      <th className="text-center px-3 py-1.5 w-40">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVentas.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center py-10 text-neutral-400">
                          {ventas.length === 0 ? "Sin ventas hoy." : "Ningún resultado con los filtros aplicados."}
                        </td>
                      </tr>
                    )}
                    {filteredVentas.map((v) => {
                      const anulada = v.estado === "anulada";
                      const tipoUp = (v.tipo_comprobante || "").toUpperCase();
                      const esFacturaFiscal = !anulada && !!v.cae && (tipoUp === "FACTURA A" || tipoUp === "FACTURA B" || tipoUp === "FACTURA C");
                      const esNotaCredito = tipoUp.includes("NOTA") && (tipoUp.includes("CREDITO") || tipoUp.includes("CRÉDITO"));
                      const puedeReimprimir = !anulada && (v.items?.length || 0) > 0;
                      const puedeAnular = !anulada && !esNotaCredito;
                      return (
                        <tr key={v.id} className={anulada
                          ? "border-t border-red-100 line-through text-red-500 bg-red-50/40"
                          : "border-t border-[#6B1F24]/10 hover:bg-[#F0D1B6]/15"}
                          data-testid={`caja-venta-${v.id}`}
                        >
                          <td className="px-3 py-1.5 font-mono text-[11px]">{fmtHora(v.created_at || v.fecha).split(",")[1]?.trim() || "—"}</td>
                          <td className="px-3 py-1.5 font-medium">{v.tipo_comprobante}</td>
                          <td className="px-3 py-1.5 font-mono">{v.numero || "—"}</td>
                          <td className="px-3 py-1.5 truncate">{v.contraparte_nombre || "Consumidor Final"}</td>
                          <td className="px-3 py-1.5 text-[11px]">
                            {(v.medios_pago || []).length === 0 ? (
                              <span className="text-neutral-300">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {v.medios_pago.map((m, i) => (
                                  <span
                                    key={`${m.caja_id || i}-${i}`}
                                    className="inline-block px-1.5 py-0.5 rounded bg-[#F0D1B6]/50 border border-[#6B1F24]/20 text-[#6B1F24] font-medium"
                                    title={m.tipo || ""}
                                  >
                                    {m.nombre || "—"}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{v.items?.length || 0}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-[10px]">
                            {v.cae ? <span className="text-emerald-700">✓</span> : <span className="text-neutral-400">—</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono font-bold text-[#6B1F24]">{fmtARS(v.total || 0)}</td>
                          <td className="px-3 py-1.5 text-center">
                            <div className="inline-flex items-center gap-1">
                              {puedeReimprimir ? (
                                <button
                                  type="button"
                                  title={v.cae ? "Reimprimir ticket fiscal por comandera" : "Reimprimir ticket (no fiscal) por comandera"}
                                  data-testid={`btn-reimprimir-${v.id}`}
                                  onClick={async () => {
                                    try {
                                      await imprimirTicketFiscal(v);
                                      toast.success(`Ticket ${v.tipo_comprobante} ${v.numero || ""} reenviado a la comandera`);
                                    } catch (err) {
                                      toast.error("No se pudo reimprimir el ticket");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-[#6B1F24] text-white hover:bg-[#571519] active:scale-95 transition"
                                >
                                  <Printer size={12} weight="bold" /> Imprimir
                                </button>
                              ) : null}
                              {puedeAnular ? (
                                <button
                                  type="button"
                                  title={esFacturaFiscal
                                    ? "Emitir Nota de Crédito electrónica (AFIP)"
                                    : "Emitir Nota de Crédito X (no fiscal)"}
                                  data-testid={`btn-nc-${v.id}`}
                                  onClick={async () => {
                                    const label = esFacturaFiscal
                                      ? `Nota de Crédito electrónica para ${v.tipo_comprobante} ${v.numero || ""}. Se pedirá CAE a AFIP y anulará la factura.`
                                      : `Nota de Crédito X para ${v.tipo_comprobante} ${v.numero || ""}. La venta quedará anulada.`;
                                    if (!window.confirm(`¿Emitir ${label}\n\nEsta acción no se puede revertir.`)) return;
                                    try {
                                      if (esFacturaFiscal) {
                                        const { data } = await api.post(`/facturas/${v.id}/nota-credito`, { auto_afip: true });
                                        toast.success(`${data.tipo_comprobante || "NC"} ${data.nota_credito_numero || data.numero || ""} emitida ✓`);
                                      } else {
                                        const { data } = await api.delete(`/facturas/${v.id}`);
                                        const ncNum = data?.nota_credito_id ? " emitida" : "";
                                        toast.success(`Venta anulada con Nota de Crédito X${ncNum}`);
                                      }
                                      loadVentas();
                                    } catch (err) {
                                      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || "No se pudo emitir la nota de crédito";
                                      toast.error(typeof msg === "string" ? msg : "Error al emitir nota de crédito");
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-amber-600 text-white hover:bg-amber-700 active:scale-95 transition"
                                >
                                  <FileText size={12} weight="bold" /> NC
                                </button>
                              ) : null}
                              {!puedeReimprimir && !puedeAnular ? (
                                <span className="text-neutral-300 text-[11px]">—</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredVentas.length > 0 && (
                      <tr className="bg-[#F0D1B6]/40 border-t-2 border-[#6B1F24]/40">
                        <td colSpan={7} className="px-3 py-2 font-heading font-bold uppercase text-[#6B1F24]">
                          {filtrosActivos ? "Total filtrado" : "Total ingresado"}
                          {totalAnuladas > 0 && !filtrosActivos && (
                            <span className="text-[10px] text-red-600 ml-2 font-mono normal-case">
                              ({totalAnuladas} anulada{totalAnuladas > 1 ? "s" : ""} excluidas)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[16px] text-[#6B1F24]">{fmtARS(filtrosActivos ? totalFiltrado : totalIngresos)}</td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {!sesionLoading && subtab === "arqueo" && (
          <ArqueoView cajaMostrador={sesion?.caja_mostrador} />
        )}

        {!sesionLoading && subtab === "ajuste" && (
          <AsientoCajaView
            key="ajuste"
            modo="ajuste"
            cajasEfectivo={sesion?.cajas_efectivo || []}
            cajaMostrador={sesion?.caja_mostrador}
            onDone={onReload}
          />
        )}

        {!sesionLoading && subtab === "alivio" && (
          <AsientoCajaView
            key="alivio"
            modo="alivio"
            cajasEfectivo={sesion?.cajas_efectivo || []}
            cajaMostrador={sesion?.caja_mostrador}
            onDone={onReload}
          />
        )}
      </main>

      {/* Dialog: Abrir caja */}
      {openDialog && (
        <AbrirCajaDialog
          cajaMostrador={sesion?.caja_mostrador}
          operador={sesion?.operador_actual}
          onCancel={() => setOpenDialog(false)}
          onDone={() => { setOpenDialog(false); onReload(); }}
        />
      )}
      {/* Dialog: Cerrar caja */}
      {closeDialog && (
        <CerrarCajaDialog
          sesion={sesion}
          onCancel={() => setCloseDialog(false)}
          onDone={() => { setCloseDialog(false); onReload(); }}
        />
      )}
    </div>
  );
}

export function AbrirCajaDialog({ cajaMostrador, operador, onCancel, onDone }) {
  const saldoSistema = Math.max(0, Number(cajaMostrador?.saldo || 0));
  const [montoContado, setMontoContado] = useState(saldoSistema.toFixed(2));
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  const contadoNum = parseFloat(montoContado) || 0;
  const diferencia = contadoNum - saldoSistema;

  const guardar = async () => {
    setSaving(true);
    try {
      await api.post("/pos/sesion/abrir", {
        saldo_inicial: contadoNum,
        saldo_sistema_apertura: saldoSistema,
        notas: notas || null,
      });
      toast.success("Caja abierta. Ya podés operar en el POS.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al abrir la caja");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="abrir-caja-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24] font-heading">
            <LinkSimple size={20} weight="bold" /> Abrir Caja
          </DialogTitle>
          <DialogDescription>
            Al abrir la caja se registra tu usuario ({operador?.username}) como responsable del turno.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#F0D1B6]/25 border border-[#6B1F24]/15 rounded p-3">
            <div className="flex justify-between items-center text-[12px]">
              <span className="text-neutral-700">Saldo por sistema</span>
              <span className="font-mono font-bold text-[#6B1F24]" data-testid="abrir-saldo-sistema">
                {fmtARS(saldoSistema)}
              </span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Caja: {cajaMostrador?.nombre || "Efectivo Mostrador"}
            </div>
          </div>
          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">
              Monto contado (arqueo real)
            </Label>
            <NumberInput
              autoFocus
              step="0.01"
              data-testid="abrir-monto-contado"
              value={montoContado}
              onChange={(e) => setMontoContado(e.target.value)}
              placeholder="0.00"
              className="mt-1 font-mono text-right text-[16px] border-[#6B1F24]/20"
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Contá el efectivo físico en la caja mostrador. Se registrará como saldo inicial del turno.
            </div>
          </div>
          <div
            className={`flex justify-between items-center rounded p-2.5 border font-mono text-[13px] ${
              Math.abs(diferencia) < 0.01
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : diferencia > 0
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="abrir-diferencia"
          >
            <span className="text-[11px] uppercase tracking-widest">Diferencia</span>
            <span className="font-bold">
              {diferencia > 0 ? "+" : ""}
              {fmtARS(diferencia)}
            </span>
          </div>
          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">Notas de apertura (opcional)</Label>
            <Input
              data-testid="abrir-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej: Turno mañana, dejé $500 de vuelto..."
              className="mt-1 border-[#6B1F24]/20"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
          <Button
            data-testid="btn-confirmar-abrir-caja"
            onClick={guardar}
            disabled={saving}
            className="bg-[#4DC247] hover:bg-[#3fa63a] gap-2"
          >
            <LinkSimple size={14} /> {saving ? "Abriendo..." : "Abrir Caja"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CerrarCajaDialog({ sesion, onCancel, onDone }) {
  const [saldoSistema, setSaldoSistema] = useState(
    Number(sesion?.caja_mostrador?.saldo || 0)
  );
  const [saldoFinal, setSaldoFinal] = useState(
    Number(sesion?.caja_mostrador?.saldo || 0).toFixed(2)
  );
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Al abrir el diálogo, traer el saldo actual del sistema (por si cambió)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pos/sesion-actual");
        const s = Number(data?.caja_mostrador?.saldo || 0);
        setSaldoSistema(s);
        setSaldoFinal(s.toFixed(2));
      } catch {}
      setLoading(false);
    })();
  }, []);

  const contadoNum = parseFloat(saldoFinal) || 0;
  const diferencia = contadoNum - saldoSistema;

  const guardar = async () => {
    if (!window.confirm("¿Cerrar la caja definitivamente? El POS quedará bloqueado hasta que se abra una nueva sesión.")) return;
    setSaving(true);
    try {
      await api.post("/pos/sesion/cerrar", {
        saldo_final_contado: saldoFinal !== "" ? parseFloat(saldoFinal) : null,
        notas: notas || null,
      });
      toast.success("Caja cerrada. El POS ya no permite ventas hasta abrir de nuevo.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cerrar la caja");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="cerrar-caja-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 font-heading">
            <LinkSimpleBreak size={20} weight="bold" /> Cerrar Caja
          </DialogTitle>
          <DialogDescription>
            Se cierra el turno del operador <strong>{sesion?.operador?.username}</strong> abierto el{" "}
            {sesion?.apertura_at ? new Date(sesion.apertura_at).toLocaleString("es-AR") : "—"}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-[#F0D1B6]/25 border border-[#6B1F24]/15 rounded p-3">
            <div className="flex justify-between items-center text-[12px]">
              <span className="text-neutral-700">Saldo actual del sistema</span>
              <span className="font-mono font-bold text-[#6B1F24]" data-testid="cerrar-saldo-sistema">
                {loading ? "..." : fmtARS(saldoSistema)}
              </span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Caja: {sesion?.caja_mostrador?.nombre || "Efectivo Mostrador"} · Saldo inicial del turno: {fmtARS(sesion?.saldo_inicial || 0)}
            </div>
          </div>
          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">Saldo final contado (arqueo)</Label>
            <NumberInput
              autoFocus
              step="0.01"
              data-testid="cerrar-saldo-final"
              value={saldoFinal}
              onChange={(e) => setSaldoFinal(e.target.value)}
              placeholder="0.00"
              className="mt-1 font-mono text-right text-[16px] border-[#6B1F24]/20"
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Precargado con el saldo del sistema. Ajustalo con el efectivo físico contado.
            </div>
          </div>
          <div
            className={`flex justify-between items-center rounded p-2.5 border font-mono text-[13px] ${
              Math.abs(diferencia) < 0.01
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : diferencia > 0
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="cerrar-diferencia"
          >
            <span className="text-[11px] uppercase tracking-widest">Diferencia</span>
            <span className="font-bold">
              {diferencia > 0 ? "+" : ""}
              {fmtARS(diferencia)}
            </span>
          </div>
          <div>
            <Label className="text-[12px] font-medium text-[#6B1F24]">Notas de cierre (opcional)</Label>
            <Input
              data-testid="cerrar-notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej: Retiré $10.000, quedó $2.500 en caja para cambio..."
              className="mt-1 border-[#6B1F24]/20"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
          <Button
            data-testid="btn-confirmar-cerrar-caja"
            onClick={guardar}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 gap-2"
          >
            <LinkSimpleBreak size={14} /> {saving ? "Cerrando..." : "Cerrar Caja"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FieldCard({ label, value, mono = false, icon: Icon, testid }) {
  return (
    <div className="border border-[#6B1F24]/15 bg-white rounded p-3" data-testid={testid}>
      <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`text-[15px] mt-0.5 font-medium text-[#6B1F24] ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

export function StatBox({ label, value, color = "text-neutral-800", wide = false, mono = false, testid }) {
  return (
    <div className={`border border-[#6B1F24]/15 bg-white rounded p-3 ${wide ? "col-span-1" : ""}`} data-testid={testid}>
      <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
      <div className={`text-[20px] font-bold mt-0.5 ${color} ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}


// ============================================================================
// ARQUEO DE EFECTIVO — Grilla de denominaciones ARS (monedas + billetes)
// ============================================================================

const DENOMINACIONES_MONEDAS = [1, 2, 5, 10, 20, 50];
const DENOMINACIONES_BILLETES = [100, 200, 500, 1000, 2000, 10000, 20000];

export function ArqueoView({ cajaMostrador, standalone = false, onDone = null }) {
  const [cantidades, setCantidades] = useState({}); // { "1000": 5, ... }
  const [ingresoLibre, setIngresoLibre] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [saldoSistema, setSaldoSistema] = useState(Number(cajaMostrador?.saldo || 0));
  const [cajaInfo, setCajaInfo] = useState(cajaMostrador || null);

  // Refrescar saldo sistema cuando la caja cambia (prop)
  useEffect(() => {
    if (cajaMostrador) {
      setSaldoSistema(Number(cajaMostrador.saldo || 0));
      setCajaInfo(cajaMostrador);
    }
  }, [cajaMostrador]);

  const reloadSaldo = useCallback(async () => {
    try {
      if (standalone && cajaMostrador?.id) {
        // Modo standalone: leo el saldo directamente de /cajas/{id}
        const { data } = await api.get(`/cajas/${cajaMostrador.id}`);
        setSaldoSistema(Number(data?.saldo || 0));
        setCajaInfo(data);
        return;
      }
      const { data } = await api.get("/pos/sesion-actual");
      if (data?.caja_mostrador) {
        setSaldoSistema(Number(data.caja_mostrador.saldo || 0));
        setCajaInfo(data.caja_mostrador);
      }
    } catch {}
  }, [standalone, cajaMostrador?.id]);

  // Auto-refrescar al entrar a la subtab (para reflejar ventas recientes)
  useEffect(() => {
    reloadSaldo();
  }, [reloadSaldo]);

  const setCant = (valor, val) => {
    const n = parseInt(val || "0", 10);
    setCantidades((c) => ({ ...c, [String(valor)]: isNaN(n) || n < 0 ? 0 : n }));
  };

  const subtotal = (valor) => (cantidades[String(valor)] || 0) * valor;
  const totalDenom = useMemo(() => {
    let s = 0;
    for (const v of [...DENOMINACIONES_MONEDAS, ...DENOMINACIONES_BILLETES]) {
      s += subtotal(v);
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantidades]);
  const ingresoLibreNum = parseFloat(ingresoLibre) || 0;
  const totalContado = totalDenom + ingresoLibreNum;
  const diferencia = totalContado - saldoSistema;

  const reset = () => {
    setCantidades({});
    setIngresoLibre("");
    setNotas("");
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        caja_id: cajaInfo?.id || cajaMostrador?.id || null,
        denominaciones: Object.fromEntries(
          Object.entries(cantidades).filter(([, v]) => (v || 0) > 0)
        ),
        ingreso_libre: ingresoLibreNum,
        notas: notas || null,
      };
      const { data } = await api.post("/pos/arqueo", payload);
      toast.success(
        `Arqueo registrado. Diferencia: ${fmtARS(data.diferencia)}`
      );
      reset();
      reloadSaldo();
      if (onDone) onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar el arqueo");
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (valor) => {
    const cant = cantidades[String(valor)] || 0;
    const sub = subtotal(valor);
    return (
      <tr key={valor} className="border-t border-[#6B1F24]/10 hover:bg-[#F0D1B6]/15">
        <td className="px-3 py-1.5 font-mono font-bold text-[#6B1F24] w-32">
          {fmtARS(valor)}
        </td>
        <td className="px-2 py-1 w-24">
          <NumberInput
            min="0"
            step="1"
            value={cant === 0 ? "" : cant}
            onChange={(e) => setCant(valor, e.target.value)}
            placeholder="0"
            data-testid={`arqueo-cant-${valor}`}
            className="h-8 text-right font-mono text-[13px] border-[#6B1F24]/20"
          />
        </td>
        <td className="px-3 py-1.5 text-right font-mono text-[13px] w-32">
          {sub > 0 ? (
            <span className="font-bold text-[#6B1F24]">{fmtARS(sub)}</span>
          ) : (
            <span className="text-neutral-400">{fmtARS(0)}</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="max-w-5xl" data-testid="arqueo-view">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-heading text-[22px] font-bold text-[#6B1F24] flex items-center gap-2">
            <Coin size={22} weight="bold" /> Arqueo de efectivo
          </h2>
          <p className="text-[12px] text-neutral-600">
            Realización del arqueo de efectivo de la caja actual ({cajaInfo?.nombre || "Efectivo Mostrador"}).
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={reloadSaldo}
          className="gap-1 border-[#6B1F24]/30 text-[#6B1F24]"
          data-testid="btn-refresh-arqueo"
        >
          Actualizar saldo
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Columna monedas */}
        <div className="border border-[#6B1F24]/20 rounded overflow-hidden bg-white">
          <div className="px-3 py-2 bg-[#F0D1B6]/40 border-b border-[#6B1F24]/20 flex items-center gap-2">
            <Coin size={16} className="text-[#6B1F24]" weight="bold" />
            <span className="font-heading text-[12px] font-bold text-[#6B1F24] uppercase">Monedas</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600">
                <tr>
                  <th className="text-left px-3 py-1.5">Valor</th>
                  <th className="text-center px-2 py-1.5">Cant.</th>
                  <th className="text-right px-3 py-1.5">Total</th>
                </tr>
              </thead>
              <tbody>{DENOMINACIONES_MONEDAS.map(renderRow)}</tbody>
            </table>
          </div>
        </div>

        {/* Columna billetes */}
        <div className="border border-[#6B1F24]/20 rounded overflow-hidden bg-white">
          <div className="px-3 py-2 bg-[#F0D1B6]/40 border-b border-[#6B1F24]/20 flex items-center gap-2">
            <Money size={16} className="text-[#6B1F24]" weight="bold" />
            <span className="font-heading text-[12px] font-bold text-[#6B1F24] uppercase">Billetes</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600">
                <tr>
                  <th className="text-left px-3 py-1.5">Valor</th>
                  <th className="text-center px-2 py-1.5">Cant.</th>
                  <th className="text-right px-3 py-1.5">Total</th>
                </tr>
              </thead>
              <tbody>{DENOMINACIONES_BILLETES.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Ingreso libre + Totales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-white border border-[#6B1F24]/20 rounded p-3">
          <Label className="text-[11px] font-medium text-[#6B1F24] uppercase tracking-widest">
            Ingreso libre
          </Label>
          <NumberInput
            step="0.01"
            min="0"
            value={ingresoLibre}
            onChange={(e) => setIngresoLibre(e.target.value)}
            placeholder="0,00"
            data-testid="arqueo-ingreso-libre"
            className="mt-1 font-mono text-right border-[#6B1F24]/20"
          />
          <div className="text-[10px] text-neutral-500 mt-1">
            Ingreso libre para importes que no encajan en denominaciones (cheques, vales, etc.).
          </div>

          <Label className="text-[11px] font-medium text-[#6B1F24] uppercase tracking-widest mt-4 block">
            Notas
          </Label>
          <Input
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Observaciones del arqueo"
            data-testid="arqueo-notas"
            className="mt-1 border-[#6B1F24]/20"
          />
        </div>

        <div className="bg-white border border-[#6B1F24]/20 rounded p-3 space-y-2">
          <div className="flex justify-between items-center text-[13px] pb-2 border-b border-neutral-100">
            <span className="text-neutral-600">Total contado</span>
            <span className="font-mono font-bold text-[#6B1F24] text-[15px]" data-testid="arqueo-total">
              {fmtARS(totalContado)}
            </span>
          </div>
          <div className="flex justify-between items-center text-[13px]">
            <span className="text-neutral-600">Total por sistema</span>
            <span className="font-mono font-bold text-neutral-800" data-testid="arqueo-total-sistema">
              {fmtARS(saldoSistema)}
            </span>
          </div>
          <div
            className={`flex justify-between items-center rounded p-2.5 border font-mono text-[14px] mt-2 ${
              Math.abs(diferencia) < 0.01
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : diferencia > 0
                ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
            data-testid="arqueo-diferencia"
          >
            <span className="text-[11px] uppercase tracking-widest flex items-center gap-1">
              {Math.abs(diferencia) < 0.01 && <CheckCircle size={13} weight="bold" />}
              Diferencia
            </span>
            <span className="font-bold text-[15px]">
              {diferencia > 0 ? "+" : ""}
              {fmtARS(diferencia)}
            </span>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          variant="outline"
          onClick={reset}
          disabled={saving}
          className="border-neutral-300"
          data-testid="btn-arqueo-cancelar"
        >
          Cancelar
        </Button>
        <Button
          onClick={guardar}
          disabled={saving || totalContado <= 0}
          className="bg-[#4DC247] hover:bg-[#3fa63a] gap-2"
          data-testid="btn-arqueo-aceptar"
        >
          <CheckCircle size={14} weight="bold" /> {saving ? "Guardando..." : "Aceptar"}
        </Button>
      </div>
    </div>
  );
}


// ============================================================================
// ASIENTO DE CAJA — Ajuste manual / Alivio (traspaso a Efectivo Mayor)
// ============================================================================

export function AsientoCajaView({ modo, cajasEfectivo, cajaMostrador, onDone }) {
  const esAlivio = modo === "alivio";
  const titulo = esAlivio ? "Alivio de caja" : "Ajuste de caja";
  const descripcion = esAlivio
    ? "Traslada efectivo desde Efectivo Mostrador hacia la caja destino configurada por administración."
    : "Registra ingresos o egresos manuales sobre una caja (retiros, faltantes, sobrantes).";
  const hoy = new Date();
  const fechaHora = `${String(hoy.getDate()).padStart(2, "0")}/${String(hoy.getMonth() + 1).padStart(2, "0")}/${hoy.getFullYear()} ${String(hoy.getHours()).padStart(2, "0")}:${String(hoy.getMinutes()).padStart(2, "0")}`;

  const [cajaId, setCajaId] = useState(cajaMostrador?.id || "");
  const [tipo, setTipo] = useState("egreso"); // sólo aplica para ajuste
  const [observaciones, setObservaciones] = useState("");
  const [items, setItems] = useState([{ descripcion: "", monto: "" }]);
  const [saving, setSaving] = useState(false);
  // Destino configurado para los alivios (viene de /pos/config/alivio-destino).
  // Si no hay ninguno seteado, mostramos la etiqueta neutra "Caja configurada".
  const [destinoInfo, setDestinoInfo] = useState(null);
  useEffect(() => {
    if (!esAlivio) return;
    (async () => {
      try {
        const { data } = await api.get("/pos/config/alivio-destino");
        setDestinoInfo(data?.caja_destino || null);
      } catch (_) {
        setDestinoInfo(null);
      }
    })();
  }, [esAlivio]);

  const addRow = () => setItems((x) => [...x, { descripcion: "", monto: "" }]);
  const rmRow = (i) => setItems((x) => x.filter((_, idx) => idx !== i));
  const updRow = (i, k, v) => setItems((x) => x.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const total = items.reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
  const hayItemsValidos = items.some((r) => (parseFloat(r.monto) || 0) > 0);

  const reset = () => {
    setItems([{ descripcion: "", monto: "" }]);
    setObservaciones("");
    setTipo("egreso");
  };

  const guardar = async () => {
    if (!hayItemsValidos) {
      toast.error("Cargá al menos un ítem con monto > 0");
      return;
    }
    if (!esAlivio && !cajaId) {
      toast.error("Elegí la caja destino del ajuste");
      return;
    }
    setSaving(true);
    try {
      const cleanItems = items
        .filter((r) => (parseFloat(r.monto) || 0) > 0)
        .map((r) => ({
          descripcion: r.descripcion.trim() || (esAlivio ? "Alivio de caja" : (tipo === "ingreso" ? "Ingreso manual" : "Retiro de capital")),
          monto: parseFloat(r.monto),
        }));
      if (esAlivio) {
        const { data } = await api.post("/pos/alivio-caja", {
          observaciones: observaciones || null,
          items: cleanItems,
        });
        toast.success(
          `Alivio registrado: ${fmtARS(data.total)} · ${data.origen.nombre} → ${data.destino.nombre}`
        );
        // Imprimir un ticket por cada ítem del alivio (uno por movimiento)
        try {
          const empresa = await api.get("/empresa/header").then((r) => r.data).catch(() => ({}));
          const sucursal = empresa?.sucursal || empresa?.nombre || empresa?.razon_social || "";
          const now = new Date();
          const fechaStr = now.toLocaleString("es-AR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
          const cajaLabel = `${data?.origen?.nombre || cajaMostrador?.nombre || ""} · ${now.toISOString().slice(0, 19).replace("T", " ")}`;
          const usuarioLabel = data?.usuario_username || data?.username || "";
          for (const it of cleanItems) {
            // eslint-disable-next-line no-await-in-loop
            await imprimirTicketAlivio({
              fecha: fechaStr,
              sucursal,
              caja: cajaLabel,
              usuario: usuarioLabel,
              comentario: it.descripcion,
              movimiento: "Egreso",
              monto: it.monto,
            });
          }
        } catch (printErr) {
          console.warn("Print alivio ticket falló:", printErr);
        }
      } else {
        const { data } = await api.post("/pos/ajuste-caja", {
          caja_id: cajaId,
          tipo,
          observaciones: observaciones || null,
          items: cleanItems,
        });
        toast.success(`Ajuste registrado: ${fmtARS(data.total)} (${data.tipo})`);
      }
      reset();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid={`asiento-caja-${modo}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-heading text-[22px] font-bold text-[#6B1F24] flex items-center gap-2">
            {esAlivio ? <ArrowLeft size={22} weight="bold" /> : <Scales size={22} weight="bold" />}
            {titulo}
          </h2>
          <p className="text-[12px] text-neutral-600">{descripcion}</p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-neutral-500 font-mono">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          Estado
        </div>
      </div>

      <div className="bg-white border border-[#6B1F24]/20 rounded overflow-hidden">
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 p-4 border-b border-[#6B1F24]/10 bg-[#F0D1B6]/15">
          <div className="text-[12px] font-medium text-[#6B1F24] pt-1">Asiento de caja</div>
          <div className="text-[13px] font-mono text-neutral-800">
            {esAlivio ? "Alivio de caja" : "Ajuste de caja"}
          </div>

          <div className="text-[12px] font-medium text-[#6B1F24] pt-2">Caja</div>
          <div>
            {esAlivio ? (
              <div className="text-[13px] font-mono text-neutral-800 py-1.5" data-testid="alivio-cajas-info">
                {cajaMostrador?.nombre || "Efectivo Mostrador"}
                <span className="text-neutral-400 mx-2">→</span>
                <span className="text-emerald-700 font-semibold">
                  {destinoInfo?.nombre || "Caja configurada"}
                </span>
                <span className="text-neutral-400 ml-3">·  {fechaHora}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  value={cajaId}
                  onChange={(e) => setCajaId(e.target.value)}
                  data-testid="asiento-caja-select"
                  className="h-9 px-2 rounded border border-[#6B1F24]/20 bg-white text-[13px] flex-1"
                >
                  {(cajasEfectivo || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  data-testid="asiento-tipo-select"
                  className="h-9 px-2 rounded border border-[#6B1F24]/20 bg-white text-[13px] w-32"
                >
                  <option value="egreso">Egreso</option>
                  <option value="ingreso">Ingreso</option>
                </select>
                <span className="text-neutral-400 text-[12px] font-mono">{fechaHora}</span>
              </div>
            )}
          </div>

          <div className="text-[12px] font-medium text-[#6B1F24] pt-2">Observaciones</div>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            data-testid="asiento-observaciones"
            rows={2}
            placeholder="(opcional)"
            className="w-full px-2 py-1.5 rounded border border-[#6B1F24]/20 text-[13px] resize-none"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600">
              <tr>
                <th className="text-center px-3 py-2">Descripción</th>
                <th className="text-right px-3 py-2 w-40">Monto</th>
                <th className="w-10 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={i} className="border-t border-neutral-100 hover:bg-[#F0D1B6]/10">
                  <td className="px-3 py-1">
                    <Input
                      value={r.descripcion}
                      onChange={(e) => updRow(i, "descripcion", e.target.value)}
                      placeholder={esAlivio ? "Traslado al mayor" : (tipo === "ingreso" ? "Aporte de socio" : "Retiro de capital")}
                      data-testid={`asiento-desc-${i}`}
                      className="h-8 border-neutral-200"
                    />
                  </td>
                  <td className="px-3 py-1">
                    <NumberInput
                      step="0.01"
                      min="0"
                      value={r.monto}
                      onChange={(e) => updRow(i, "monto", e.target.value)}
                      placeholder="0,00"
                      data-testid={`asiento-monto-${i}`}
                      className="h-8 font-mono text-right border-neutral-200"
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => rmRow(i)}
                        className="text-neutral-400 hover:text-red-600 p-1"
                        data-testid={`asiento-rm-${i}`}
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="px-3 py-1.5 text-center border-t border-neutral-100">
                  <button
                    type="button"
                    onClick={addRow}
                    className="text-[11px] text-[#6B1F24] hover:underline flex items-center gap-1 mx-auto"
                    data-testid="asiento-add-row"
                  >
                    <Plus size={12} /> Agregar fila
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#6B1F24]/30 bg-[#F0D1B6]/10">
                <td className="px-3 py-2 text-right text-[12px] uppercase font-mono text-neutral-600">Valores</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[15px] text-[#6B1F24]" data-testid="asiento-total">
                  {fmtARS(total)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button
          variant="outline"
          onClick={reset}
          disabled={saving}
          className="border-neutral-300"
          data-testid="btn-asiento-cancelar"
        >
          Cancelar
        </Button>
        <Button
          onClick={guardar}
          disabled={saving || !hayItemsValidos}
          className={esAlivio ? "bg-[#6B1F24] hover:bg-[#8F191C] gap-2" : "bg-[#4DC247] hover:bg-[#3fa63a] gap-2"}
          data-testid="btn-asiento-aceptar"
        >
          <CheckCircle size={14} weight="bold" /> {saving ? "Guardando..." : "Aceptar"}
        </Button>
      </div>
    </div>
  );
}
